/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

const badges = document.querySelectorAll(".product__badge");

function updateBadge(variant) {
    if (!variant) return;

    if (variant.compare_at_price > variant.price) {
        const diff = variant.compare_at_price - variant.price;
        const discount = Math.round((diff * 100) / variant.compare_at_price);
        badges.forEach(function (badge) {
            const discountEl = badge?.querySelector(".product__badge-discount");
            discountEl.textContent = `-${discount}%`;
            badge.hidden = false;
        });
    } else {
        badges.forEach(function (badge) {
            badge.hidden = true;
        });
    }
}

document.addEventListener('variant:changed', function (event) {
    //console.log('variant:change');
    let variant = event.detail.variant; // Gives you access to the whole variant details
    if (!variant) return;
    if (variant?.id) updateBadge(variant);

    const preorderMapEl = document.querySelector('[data-variant-preorder-map]');
    if (!preorderMapEl) return;

    const preorderMap = JSON.parse(preorderMapEl.textContent);
    const isPreorder = preorderMap[String(variant.id)];

    const buttonTextEl = document.querySelector('[data-add-to-cart-text],#AddToCart .loader-button__text');
    if (!buttonTextEl) return;

    let button = buttonTextEl.closest('#AddToCart');
    let labelPreOrder;
    let labelAddToCart;
    if (button) {
        labelPreOrder = button.getAttribute('data-label-preorder');
        labelAddToCart = button.getAttribute('data-label-addtocart');
    }
    buttonTextEl.textContent = isPreorder ? (labelPreOrder ? labelPreOrder : 'Preorder') : (labelAddToCart ? labelAddToCart : 'Add to cart');
});


(function () {
    const FEEDATY_SCRIPT_PART = 'widget.feedaty.com/v3.0.0/js/2021/';
    const FEEDATY_MERCHANT = '10214261'; // mettilo giusto per Foxydry
    const FEEDATY_SCRIPT_SRC = `https://widget.feedaty.com/v3.0.0/js/2021/${FEEDATY_MERCHANT}/feedaty.min.js`;

    function onPageReady(cb) {
        if (document.readyState === 'complete') cb();
        else window.addEventListener('load', cb, {once: true});
    }

    function ensureFeedatyScriptOnce() {
        // Se l'app lo ha già iniettato, non fare nulla
        const already = Array.from(document.scripts).some(s =>
            (s.src || '').includes(FEEDATY_SCRIPT_PART) && (s.src || '').includes(`/${FEEDATY_MERCHANT}/`)
        );
        if (already) return;

        // Evita duplicati se qualcuno lo ha messo con src identico
        const alreadyExact = Array.from(document.scripts).some(s => (s.src || '') === FEEDATY_SCRIPT_SRC);
        if (alreadyExact) return;

        const s = document.createElement('script');
        s.async = true;
        s.type = 'text/javascript';
        s.src = FEEDATY_SCRIPT_SRC;
        document.body.appendChild(s);
    }

    function setupWidgetDom(root) {
        if (!root || root.dataset.feedatyDomReady === '1') return;
        root.dataset.feedatyDomReady = '1';

        // Se dentro Feedaty genera <a>, togliamo href e mettiamo pointer
        const link = root.querySelector('a');
        if (link) {
            link.removeAttribute('href');
            link.style.cursor = 'pointer';
        }

        // Mostra sempre (se avevi CSS che lo nasconde)
        root.style.display = 'block';
    }

    function setupAllWidgetsIn(container) {
        const scope = container || document;
        const nodes = scope.querySelectorAll('.feedaty_widget');
        nodes.forEach(setupWidgetDom);
    }

    // "Kick" di re-render: non avendo API pubblica certa, facciamo una strategia safe.
    // Molti widget bootstrap si attaccano a DOMContentLoaded/load: rilanciamo eventi *una sola volta per batch*.
    let kickTimer = null;

    function kickFeedatyBootstrap() {
        if (kickTimer) return;
        kickTimer = setTimeout(() => {
            kickTimer = null;

            // 1) assicurati che lo script ci sia (senza duplicarlo)
            ensureFeedatyScriptOnce();

            // 2) prepara DOM dei widget
            setupAllWidgetsIn(document);

            // 3) Trigger "soft" (non reinietta script → evita 'already declared')
            //    NB: sì, è un workaround: serve quando i widget arrivano dopo load.
            try {
                document.dispatchEvent(new Event('DOMContentLoaded'));
            } catch (e) {
            }
            try {
                window.dispatchEvent(new Event('load'));
            } catch (e) {
            }
        }, 50);
    }

    // Primo run
    onPageReady(function () {
        setupAllWidgetsIn(document);
        kickFeedatyBootstrap();
    });

    // Quando Focal/Shopify ricarica sezioni (Theme Editor + alcune AJAX)
    document.addEventListener('shopify:section:load', function (e) {
        setupAllWidgetsIn(e.target);
        kickFeedatyBootstrap();
    });

    // Caso principale: slider/product-recommendations che inietta HTML dopo
    const obs = new MutationObserver(function (mutations) {
        for (const mu of mutations) {
            if (!mu.addedNodes || !mu.addedNodes.length) continue;

            // Se tra i nodi aggiunti c'è un feedaty_widget (o lo contiene), kick
            const added = Array.from(mu.addedNodes);
            const hasFeedaty = added.some(n =>
                n.nodeType === 1 && (n.matches?.('.feedaty_widget') || n.querySelector?.('.feedaty_widget'))
            );
            if (hasFeedaty) {
                added.forEach(n => n.nodeType === 1 && setupAllWidgetsIn(n));
                kickFeedatyBootstrap();
                break;
            }
        }
    });

    obs.observe(document.documentElement, {childList: true, subtree: true});
})();

(function () {
    document.addEventListener('click', function (e) {
        const widget = e.target.closest('.feedaty_widget');
        if (!widget) return;

        const selector = widget.getAttribute('data-feedaty-click-scroll');
        if (!selector) return; // niente attributo = nessuno scroll

        const offset = parseInt(widget.getAttribute('data-feedaty-scroll-offset') || '0', 10);
        const target = document.querySelector(selector);
        if (!target) return;

        e.preventDefault();

        const top = target.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({
            top: top,
            behavior: 'smooth'
        });
    });
})();


/* ---------------------------------------------------------------------------
 * MEDIA GROUPING — ordine immagini + featured_media, language-agnostic
 * --------------------------------------------------------------------------- */

(function () {

    function readGroupData(productMediaEl) {
        const script = productMediaEl.querySelector('.js-variant-media-groups');
        if (!script) return null;
        try { return JSON.parse(script.textContent); }
        catch (e) { console.warn('[media-grouping] JSON parse error:', e); return null; }
    }

    function findProductMedia(event) {
        const formId = event.detail?.formId
            || (event.target instanceof HTMLFormElement ? event.target.id : null);
        if (formId) {
            const el = document.querySelector(`product-media[form-id="${formId}"]`);
            if (el) return el;
        }
        const all = document.querySelectorAll('product-media');
        if (all.length === 1) return all[0];
        console.warn('[media-grouping] product-media non trovato. formId:', formId);
        return null;
    }

    document.addEventListener('variant:changed', function (event) {
        const variant = event.detail.variant;
        if (!variant) return;

        const productMediaEl = findProductMedia(event);
        if (!productMediaEl) return;

        const groupData = readGroupData(productMediaEl);
        if (!groupData) return;

        const carousel = productMediaEl.querySelector('flickity-carousel');
        if (!carousel) return;

        productMediaEl.style.visibility = 'hidden';

        setTimeout(async function () {
            const { featuredMediaByVariant, mediaGroups } = groupData;
            const variantId = String(variant.id);

            // --- targetId ---
            // Il targetId è il variantId del CAPOGRUPPO — cioè la variante
            // a cui il blob mediaGroups assegna la featured_media della variante
            // selezionata. Questa può essere diversa dalla variante selezionata
            // stessa se più varianti condividono la stessa immagine featured.
            // Es: variante 50120023408967 ha featured_media 55048941207879
            //     mediaGroups[55048941207879].variantId = 52683800117575  ← capogruppo
            //     → targetId = 52683800117575
            let targetId = null;
            if (variant.featured_media) {
                const myFmId = String(variant.featured_media.id);
                const entry = mediaGroups[myFmId];
                if (entry && !entry.allGroups && !entry.noGroup && entry.variantId) {
                    // Il capogruppo è quello assegnato dal blob
                    targetId = String(entry.variantId);
                } else if (entry && entry.noGroup) {
                    // featured_media senza gruppo → nessun filtro
                    targetId = null;
                } else {
                    // Fallback: scorri i media in ordine DOM
                    const mediaOrder = Array.from(
                        productMediaEl.querySelectorAll('[data-media-id][data-original-position]')
                    ).sort((a, b) =>
                        parseInt(a.getAttribute('data-original-position')) -
                        parseInt(b.getAttribute('data-original-position'))
                    ).map(el => el.getAttribute('data-media-id'));
                    let last = null;
                    for (const mid of mediaOrder) {
                        const g = mediaGroups[mid];
                        if (g && !g.allGroups && !g.noGroup && g.variantId) last = String(g.variantId);
                        if (mid === myFmId) break;
                    }
                    targetId = last;
                }
            }

            // --- toFilter ---
            const toFilter = new Set();
            if (targetId !== null) {
                Object.entries(mediaGroups).forEach(function ([mediaId, group]) {
                    if (group.allGroups) return;
                    if (group.noGroup) { toFilter.add(mediaId); return; }
                    if (String(group.variantId) !== targetId) toFilter.add(mediaId);
                });
            }
            // --- changed? ---
            let changed = false;
            productMediaEl.querySelectorAll('[data-media-id]').forEach(function (item) {
                const should = toFilter.has(item.getAttribute('data-media-id'));
                if (item.classList.contains('is-filtered') !== should) changed = true;
            });

            if (changed) {
                const flickityInstance = await carousel.flickityInstance;
                flickityInstance.destroy();

                const fmId = featuredMediaByVariant[variantId]
                    ? String(featuredMediaByVariant[variantId])
                    : null;
                const orderedIds = Array.from(
                    productMediaEl.querySelectorAll('[data-media-id][data-original-position]')
                ).sort((a, b) =>
                    parseInt(a.getAttribute('data-original-position')) -
                    parseInt(b.getAttribute('data-original-position'))
                ).map(el => el.getAttribute('data-media-id'));
                const firstVisible = (fmId && !toFilter.has(fmId))
                    ? fmId
                    : orderedIds.find(id => !toFilter.has(id)) || null;

                productMediaEl.querySelectorAll('[data-media-id]').forEach(function (item) {
                    const id = item.getAttribute('data-media-id');
                    item.classList.toggle('is-filtered', toFilter.has(id));
                    item.classList.toggle('is-selected', id === firstVisible);
                    item.classList.toggle('is-initial-selected', id === firstVisible);
                });

                carousel.reload();
            } else {
                const fmId = featuredMediaByVariant[variantId];
                if (fmId) {
                    const target = productMediaEl.querySelector(`[data-media-id="${fmId}"]`);
                    if (target && !target.classList.contains('is-filtered')) {
                        carousel.select(`[data-media-id="${fmId}"]`);
                    }
                }
            }

            productMediaEl.style.visibility = '';
        }, 0);
    });

})();